import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Ai, Calendar, CheckCircleLine, Person } from '@factorialco/f0-react/icons/app'
import { OneOnOnesTeamTableEmployeesDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useOneOnOnesContext } from 'modules/meetings/lib/hooks/useOneOnOnesContext'
import { MeetingEmployee, getTodayMeetingTag } from 'modules/meetings/lib/utils/teamTableSorts'

interface TeamTableProps {
  setOpenCreateScheduledMeeting: (open: boolean) => void
  initialEmployees: MeetingEmployee[]
  toggleAiChat?: () => void
}
export const TeamTable = ({
  setOpenCreateScheduledMeeting,
  initialEmployees,
  toggleAiChat,
}: TeamTableProps) => {
  const { navigation } = useNavigation()
  const { canSeeAdvancedMeetings } = useOneOnOnesContext()
  const navigate = useNavigate()
  const employeeIds = useMemo(
    () => initialEmployees.map((employee) => employee.id),
    [initialEmployees]
  )

  const source = useGraphqlDataCollectionSource({
    document: OneOnOnesTeamTableEmployeesDocument,
    mapData: (data): MeetingEmployee[] => {
      return initialEmployees.filter((employee) =>
        data.employees.employeesConnection.nodes.some((node) => node.id === employee.id)
      )
    },
    mapPagination: (data) => ({
      total: data.employees.employeesConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search, filters }) => {
      const onlyMyReportees = filters?.team?.includes('team') ? true : false
      const onlyDirectReports = filters?.team?.includes('direct_reports') ? true : false

      return {
        search: search ?? '',
        ids: employeeIds,
        onlyMyReportees,
        onlyDirectReports,
      }
    },
    primaryActions: () => {
      return {
        label: i18n.t('meetings.one_on_ones.overview_table.schedule_new_meeting'),
        icon: Calendar,
        onClick: () => setOpenCreateScheduledMeeting(true),
      }
    },
    secondaryActions: {
      expanded: 1,
      actions: () =>
        toggleAiChat
          ? [
              {
                label: i18n.t('meetings.one_on_ones.overview_table.chat'),
                icon: Ai,
                onClick: () => toggleAiChat?.(),
              },
            ]
          : undefined,
    },
    itemUrl: ({ id, nextMeeting }) => {
      if (nextMeeting?.meetingId) {
        return navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
          meetingId: nextMeeting.meetingId,
          employeeId: id,
        })
      } else if (nextMeeting?.templateID) {
        return navigation.engagement.one_on_ones.templateId.fullPath({
          templateId: nextMeeting.templateID,
        })
      } else {
        return navigation.engagement.one_on_ones.employeeId.fullPath({ employeeId: id })
      }
    },
    search: {
      enabled: true,
    },
    filters: {
      team: {
        label: i18n.t('meetings.one_on_ones.overview_table.filters.team.label'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t(
                'meetings.one_on_ones.overview_table.filters.team.options.direct_reports'
              ),
              value: 'direct_reports',
            },
            {
              label: i18n.t('meetings.one_on_ones.overview_table.filters.team.options.team'),
              value: 'team',
            },
          ],
        },
      },
    },
    presets: [
      {
        label: i18n.t('meetings.one_on_ones.overview_table.presets.team'),
        filter: { team: ['team'] },
      },
      {
        label: i18n.t('meetings.one_on_ones.overview_table.presets.direct_reports'),
        filter: { team: ['direct_reports'] },
      },
    ],
    itemActions: ({ id, nextMeeting }) =>
      [
        nextMeeting &&
          canSeeAdvancedMeetings &&
          (getTodayMeetingTag(nextMeeting).value.status === 'info' ||
            getTodayMeetingTag(nextMeeting).value.status === 'warning') && {
            type: 'primary' as const,
            label: i18n.t('meetings.one_on_ones.overview_table.item_actions.join'),
            onClick: () => {
              if (nextMeeting?.meetingId) {
                navigate(
                  navigation.engagement.one_on_ones.employeeId.meetingId.prejoin.fullPath({
                    meetingId: nextMeeting.meetingId,
                    employeeId: id,
                  })
                )
              } else if (nextMeeting?.templateID) {
                navigate(
                  navigation.engagement.one_on_ones.templateId.prejoin.fullPath({
                    templateId: nextMeeting.templateID,
                  })
                )
              }
            },
          },
        {
          label: i18n.t('meetings.one_on_ones.overview_table.item_actions.view_profile'),
          onClick: () => {
            navigate(navigation.employees.id.fullPath({ id }))
          },
        },
      ].filter(Boolean),
  })

  const getPersonTypeTag = ({
    isManager,
    directReport,
    isDirectManager,
  }: {
    isManager: boolean
    directReport: boolean
    isDirectManager: boolean
  }) => {
    return {
      type: 'tag' as const,
      value: {
        label: isManager
          ? isDirectManager
            ? i18n.t('meetings.one_on_ones.overview_table.employee_type.direct_manager')
            : i18n.t('meetings.one_on_ones.overview_table.employee_type.manager')
          : directReport
          ? i18n.t('meetings.one_on_ones.overview_table.employee_type.direct_report')
          : i18n.t('meetings.one_on_ones.overview_table.employee_type.indirect_report'),
      },
    }
  }

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'list',
          options: {
            itemDefinition: (item: MeetingEmployee) => ({
              title: item.fullName ?? '',
              description: [item.referenceContract?.jobTitle, item.nextMeeting?.text].filter(
                Boolean
              ),
              avatar: {
                type: 'person',
                firstName: item.firstName ?? '',
                lastName: item.lastName ?? '',
                src: buildUrl(item.avatar?.url),
              },
            }),
            fields: [
              {
                label: i18n.t('meetings.one_on_ones.overview_table.headers.scheduled_meeting'),
                render: ({ nextMeeting }) => getTodayMeetingTag(nextMeeting),
              },
              {
                label: i18n.t('meetings.one_on_ones.overview_table.headers.type'),
                render: ({ isManager, directReport, isDirectManager }) => {
                  return getPersonTypeTag({ isManager, directReport, isDirectManager })
                },
              },
            ],
          },
        },
        {
          type: 'card',
          options: {
            title: (item) => item.fullName ?? '',
            description: (item) => item.referenceContract?.jobTitle ?? '',
            avatar: (item) => ({
              type: 'person',
              firstName: item.firstName ?? '',
              lastName: item.lastName ?? '',
              src: buildUrl(item.avatar?.url),
            }),
            cardProperties: [
              {
                label: i18n.t('meetings.one_on_ones.overview_table.headers.last_meeting'),
                icon: Calendar,
                render: (item) => item.nextMeeting?.text ?? '-',
              },
              {
                label: i18n.t('meetings.one_on_ones.overview_table.headers.type'),
                icon: Person,
                render: ({ isManager, directReport, isDirectManager }) => {
                  return getPersonTypeTag({ isManager, directReport, isDirectManager })
                },
              },
              {
                label: i18n.t('meetings.one_on_ones.overview_table.headers.scheduled_meeting'),
                icon: CheckCircleLine,
                render: ({ nextMeeting }) => getTodayMeetingTag(nextMeeting),
              },
            ],
          },
        },
      ]}
    />
  )
}
